import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { OptimalWallet } from '../interface/OptimalWallet';
import { SseService } from './SseService';

export interface RiesgoAcumulado {
  tiporiesgo: string;
  valoriresgo: number;
}

/**
 * Fusion de GET parametros/{idcarteraoptima} sobre un OptimalWallet, compartida entre
 * DiversificadorComponent (arma divOptimalWallet desde ?idcarteraoptima=... de la URL) y
 * SincronizarComparativaDialogComponent ("Cartera actual", que necesita el mismo resultado que
 * produce "Análisis De Cartera" en Diversificador para poder compararlo con la cartera real).
 * Extraído a un servicio en vez de duplicado para que ambos flujos no puedan divergir con el tiempo.
 */
@Injectable({ providedIn: 'root' })
export class CarteraOptimaParamsService {

  constructor(private sseService: SseService) {}

  /**
   * "Sincronizar cartera óptima": carga parametros/{id} de cada idcarteraoptima del CSV (uno por
   * subcartera) y los fusiona sobre `wallet` (mutándolo in-place), sin guardar. Si las subcarteras
   * tienen riesgo distinto (MIXTO/-1) se conserva el riesgo actual de la cartera, porque tbl_cartera
   * no admite MIXTO. Los derivados se recalculan igual que ReconfiguracionDialogComponent.calcularData().
   */
  sincronizarDesdeOptimas(wallet: OptimalWallet, idsCsv: string | number | null | undefined): Observable<{ aplicadas: number; riesgoMixto: boolean }> {
    const ids = Array.from(new Set(
      String(idsCsv ?? '').split(',').map(id => Number(id.trim())).filter(id => id > 0)
    ));
    if (ids.length === 0) return of({ aplicadas: 0, riesgoMixto: false });

    return forkJoin(ids.map(id =>
      this.sseService.apiGetNew('parametros/' + id).pipe(catchError(() => of(null)))
    )).pipe(
      map((respuestas: any[]) => {
        const tiporiesgoOriginal = wallet.tiporiesgo;
        const riesgoOriginal = wallet.riesgo;
        const riesgoAcumulado: RiesgoAcumulado = { tiporiesgo: '', valoriresgo: 0 };
        let aplicadas = 0;
        respuestas.forEach(carteraData => {
          if (!carteraData) return;
          this.fusionarUnaCarteraOptima(wallet, carteraData, riesgoAcumulado);
          aplicadas++;
        });

        const riesgoMixto = riesgoAcumulado.tiporiesgo === 'MIXTO' || riesgoAcumulado.valoriresgo === -1;
        if (riesgoMixto) {
          wallet.tiporiesgo = tiporiesgoOriginal;
          wallet.riesgo = riesgoOriginal;
        }
        if (aplicadas > 0) {
          wallet.efectivo = wallet.capitalinicial;
          wallet.valorLiquidativo = wallet.capitalinicial;
          wallet.fondosDisponibles = wallet.capitalinicial;
          wallet.poderCompra = wallet.capitalinicial * wallet.apalancamiento;
        }
        return { aplicadas, riesgoMixto };
      })
    );
  }

  /**
   * Fusiona la respuesta de un GET parametros/{idcarteraoptima} sobre `wallet`, mutándolo in-place.
   * La mayoría de campos usan "gana el último" (cada llamada sucesiva sobreescribe); riesgo/tiporiesgo
   * usan MIXTO/-1 si los valores difieren entre llamadas sucesivas (varias subcarteras con distinta
   * config de riesgo). `riesgoAcumulado` lleva ese estado a través de varias llamadas y también se
   * muta in-place — el llamador debe reutilizar el mismo objeto para toda una suscripción/wallet.
   */
  fusionarUnaCarteraOptima(wallet: OptimalWallet, carteraData: any, riesgoAcumulado: RiesgoAcumulado): void {
    if (!carteraData) return;

    if (carteraData.capitalinicial !== undefined) wallet.capitalinicial = carteraData.capitalinicial;
    if (carteraData.tipocuenta !== undefined) wallet.tipocuenta = carteraData.tipocuenta;

    // Margen y controles
    if (carteraData.margen !== undefined) wallet.margen = carteraData.margen;
    if (carteraData.margenRequeridoPor !== undefined) wallet.margenRequeridoPor = carteraData.margenRequeridoPor;
    if (carteraData.controlMarginCall !== undefined) wallet.controlMarginCall = carteraData.controlMarginCall;

    // Riesgo
    if (carteraData.riesgomaximo !== undefined) wallet.riesgomaximo = carteraData.riesgomaximo;
    if (carteraData.tiporiesgo !== undefined) {
      if (riesgoAcumulado.tiporiesgo === '') riesgoAcumulado.tiporiesgo = carteraData.tiporiesgo;
      if (riesgoAcumulado.tiporiesgo !== carteraData.tiporiesgo) riesgoAcumulado.tiporiesgo = 'MIXTO';
      wallet.tiporiesgo = riesgoAcumulado.tiporiesgo;
    }
    if (carteraData.riesgo !== undefined) {
      if (riesgoAcumulado.valoriresgo === 0) riesgoAcumulado.valoriresgo = carteraData.riesgo;
      if (riesgoAcumulado.valoriresgo !== carteraData.riesgo) riesgoAcumulado.valoriresgo = -1;
      wallet.riesgo = riesgoAcumulado.valoriresgo;
    }

    // Comisiones y gastos
    if (carteraData.comisionbroker !== undefined) wallet.comisionbroker = carteraData.comisionbroker;
    if (carteraData.gastosGestionPor !== undefined) wallet.gastosGestionPor = carteraData.gastosGestionPor;
    if (carteraData.gastosPrestamoPor !== undefined) wallet.gastosPrestamoPor = carteraData.gastosPrestamoPor;

    // Checks de gastos y comisiones
    if (carteraData.chkgastogest !== undefined) wallet.chkgastogest = carteraData.chkgastogest;
    if (carteraData.chkgastoprest !== undefined) wallet.chkgastoprest = carteraData.chkgastoprest;
    if (carteraData.chkcomisionbroker !== undefined) wallet.chkcomisionbroker = carteraData.chkcomisionbroker;

    // Valores liquidativos y fondos
    if (carteraData.valorLiquidativo !== undefined) wallet.valorLiquidativo = carteraData.valorLiquidativo;
    if (carteraData.efectivo !== undefined) wallet.efectivo = carteraData.efectivo;
    if (carteraData.fondosDisponibles !== undefined) wallet.fondosDisponibles = carteraData.fondosDisponibles;
    if (carteraData.poderCompra !== undefined) wallet.poderCompra = carteraData.poderCompra;

    // Opciones adicionales
    if (carteraData.participacion !== undefined) wallet.participacion = carteraData.participacion;
    if (carteraData.apalancamiento !== undefined) wallet.apalancamiento = carteraData.apalancamiento;
  }
}
